<template>
  <div class="component-catalog">
    <h5 class="fw-bold mb-2">Component Catalog</h5>

    <!-- Active slot indicator -->
    <div
      v-if="activeSlot"
      class="active-slot-badge mb-3 p-2 rounded"
      :class="{ 'slot-badge-pulse': badgeAnimating }"
    >
      <div class="d-flex align-items-center gap-2">
        <span
          v-if="activeSlotAccepts"
          class="slot-type-dot"
          :class="`slot-type-dot--${activeSlotAccepts}`"
          aria-hidden="true"
        ></span>
        <div>
          <small class="text-muted d-block" style="line-height: 1.1"
            >Assigning to:</small
          >
          <strong>{{ activeSlotLabel }}</strong>
          <span
            class="badge ms-1"
            :class="{
              'bg-primary': activeSlotAccepts === 'pipe',
              'bg-warning text-dark': activeSlotAccepts === 'fitting',
              'bg-success': activeSlotAccepts === 'valve',
              'bg-info text-dark': activeSlotAccepts === 'pump',
              'bg-secondary': !activeSlotAccepts
            }"
            >{{ activeSlotAccepts }}</span
          >
        </div>
      </div>
      <p class="mb-0 mt-1 small text-muted">
        Select a compatible component below
      </p>
    </div>
    <div v-else class="text-muted small mb-3 text-center fst-italic">
      Click a component to place it, or click a slot in the diagram first.
    </div>

    <!-- ── Pipe size/length selector (shown when a pipe is being configured) ── -->
    <div
      v-if="pendingPipe"
      ref="pipeConfig"
      class="pipe-config card mb-3 border-primary"
    >
      <div class="card-body p-2">
        <div class="fw-semibold small mb-2">{{ pendingPipe.name }}</div>

        <!-- Material incompatibility warning -->
        <div
          v-if="pendingPipe.material && !isMaterialOk(pendingPipe.material)"
          class="alert alert-danger py-1 px-2 small mb-2"
          role="alert"
        >
          <i
            class="bi bi-exclamation-triangle-fill me-1"
            aria-hidden="true"
          ></i>
          {{ pendingPipe.material }} is not compatible with {{ fluidName }}.
        </div>

        <label
          class="form-label small mb-1"
          :for="'pipe-size-' + pendingPipe.id"
          >Nominal Size (in)</label
        >
        <select
          :id="'pipe-size-' + pendingPipe.id"
          class="form-select form-select-sm mb-2"
          v-model="selectedSizeIndex"
        >
          <option v-for="(s, idx) in pendingPipe.sizes" :key="idx" :value="idx">
            {{ s.nominal }}&Prime; &mdash; ID {{ s.innerDiameter_in }}&Prime;
            &mdash; ${{ s.pricePerFoot }}/ft
          </option>
        </select>

        <label class="form-label small mb-1" for="pipe-length"
          >Length (ft)</label
        >
        <input
          id="pipe-length"
          type="number"
          class="form-control form-control-sm mb-2"
          v-model.number="pipeLength"
          :min="MIN_PIPE_LENGTH_FT"
          :max="MAX_PIPE_LENGTH_FT"
          step="1"
        />

        <div class="d-flex justify-content-between align-items-center">
          <span class="small fw-semibold">
            Cost: ${{ pipeCost.toFixed(2) }}
          </span>
          <div class="d-flex gap-1">
            <button
              class="btn btn-sm btn-outline-secondary"
              @click="cancelPipe"
            >
              Cancel
            </button>
            <button
              class="btn btn-sm btn-primary"
              @click="confirmPipe"
              :disabled="pipeLength < MIN_PIPE_LENGTH_FT"
            >
              Assign
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Pipes -->
    <h6 ref="sectionPipe" class="catalog-section-title fw-semibold">Pipes</h6>
    <div
      v-for="pipe in catalog.pipes"
      :key="pipe.id"
      class="catalog-item d-flex align-items-center mb-2 p-2 border rounded"
      :class="{
        'catalog-item--compatible': activeSlotAccepts === 'pipe',
        'catalog-item--incompatible':
          activeSlot && activeSlotAccepts !== 'pipe',
        'catalog-item--warn':
          activeSlotAccepts === 'pipe' &&
          pipe.material &&
          !isMaterialOk(pipe.material)
      }"
      role="button"
      tabindex="0"
      @click="onPipeClick(pipe)"
      @keydown.enter="onPipeClick(pipe)"
      @keydown.space.prevent="onPipeClick(pipe)"
      :aria-label="pipe.name + ' — ' + pipe.sizes.length + ' sizes'"
    >
      <div class="catalog-icon me-2">
        <CatalogIcon category="pipe" :pipe-color="pipeColor(pipe.material)" />
      </div>
      <div class="catalog-info flex-grow-1">
        <div class="fw-semibold small">{{ pipe.name }}</div>
        <div class="text-muted catalog-price">
          {{ pipe.sizes.length }} sizes &ndash; ${{
            pipe.sizes[0].pricePerFoot
          }}&ndash;${{ pipe.sizes[pipe.sizes.length - 1].pricePerFoot }}/ft
        </div>
      </div>
      <i
        v-if="pipe.material && !isMaterialOk(pipe.material)"
        class="bi bi-exclamation-triangle-fill text-danger ms-1"
        aria-hidden="true"
      ></i>
      <span
        v-if="pipe.material && !isMaterialOk(pipe.material)"
        class="visually-hidden"
        >Warning: {{ pipe.material }} is not compatible with
        {{ fluidName }}</span
      >
    </div>

    <!-- ── Fitting material selector (shown when a fitting is being configured) ── -->
    <div
      v-if="pendingFitting"
      ref="fittingConfig"
      class="fitting-config card mb-3 border-warning"
    >
      <div class="card-body p-2">
        <div class="fw-semibold small mb-2">{{ pendingFitting.name }}</div>

        <!-- Material incompatibility warning -->
        <div
          v-if="
            selectedFittingMaterial && !isMaterialOk(selectedFittingMaterial)
          "
          class="alert alert-danger py-1 px-2 small mb-2"
          role="alert"
        >
          <i
            class="bi bi-exclamation-triangle-fill me-1"
            aria-hidden="true"
          ></i>
          {{ selectedFittingMaterial }} is not compatible with {{ fluidName }}.
        </div>

        <label
          class="form-label small mb-1"
          :for="'fitting-material-' + pendingFitting.id"
          >Material</label
        >
        <select
          :id="'fitting-material-' + pendingFitting.id"
          class="form-select form-select-sm mb-2"
          v-model="selectedFittingMaterial"
        >
          <option
            v-for="mat in pendingFitting.materials"
            :key="mat"
            :value="mat"
          >
            {{ mat }}
            <template v-if="!isMaterialOk(mat)"> (incompatible)</template>
          </option>
        </select>

        <div class="d-flex justify-content-between align-items-center">
          <span class="small fw-semibold">
            Cost: ${{ pendingFitting.price }}
          </span>
          <div class="d-flex gap-1">
            <button
              class="btn btn-sm btn-outline-secondary"
              @click="cancelFitting"
            >
              Cancel
            </button>
            <button class="btn btn-sm btn-warning" @click="confirmFitting">
              Assign
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Fittings -->
    <h6 ref="sectionFitting" class="catalog-section-title fw-semibold mt-3">
      Fittings
    </h6>
    <div
      v-for="fitting in catalog.fittings"
      :key="fitting.id"
      class="catalog-item d-flex align-items-center mb-2 p-2 border rounded"
      :class="{
        'catalog-item--compatible': activeSlotAccepts === 'fitting',
        'catalog-item--incompatible':
          activeSlot && activeSlotAccepts !== 'fitting'
      }"
      role="button"
      tabindex="0"
      @click="onFittingClick(fitting)"
      @keydown.enter="onFittingClick(fitting)"
      @keydown.space.prevent="onFittingClick(fitting)"
      :aria-label="fitting.name + ' — $' + fitting.price"
    >
      <div class="catalog-icon me-2">
        <CatalogIcon category="fitting" :icon="fitting.icon" />
      </div>
      <div class="catalog-info flex-grow-1">
        <div class="fw-semibold small">{{ fitting.name }}</div>
        <div class="text-muted catalog-price">
          {{ fitting.materials.join(", ") }} &ndash; ${{ fitting.price }}
        </div>
      </div>
      <i
        v-if="!fittingMaterialOk(fitting)"
        class="bi bi-exclamation-triangle-fill text-danger ms-1"
        aria-hidden="true"
      ></i>
      <span v-if="!fittingMaterialOk(fitting)" class="visually-hidden"
        >Warning: {{ fitting.materials.join("/") }} not compatible with
        {{ fluidName }}</span
      >
    </div>

    <!-- Valves -->
    <h6 ref="sectionValve" class="catalog-section-title fw-semibold mt-3">
      Valves
    </h6>
    <div
      v-for="valve in catalog.valves"
      :key="valve.id"
      class="catalog-item d-flex align-items-center mb-2 p-2 border rounded"
      :class="{
        'catalog-item--compatible': activeSlotAccepts === 'valve',
        'catalog-item--incompatible':
          activeSlot && activeSlotAccepts !== 'valve'
      }"
      role="button"
      tabindex="0"
      @click="onSelect({ ...valve, category: 'valve' })"
      @keydown.enter="onSelect({ ...valve, category: 'valve' })"
      @keydown.space.prevent="onSelect({ ...valve, category: 'valve' })"
      :aria-label="valve.name + ' — $' + valve.price"
    >
      <div class="catalog-icon me-2">
        <CatalogIcon category="valve" :icon="valve.icon" />
      </div>
      <div class="catalog-info">
        <div class="fw-semibold small">{{ valve.name }}</div>
        <div class="text-muted catalog-price">${{ valve.price }}</div>
      </div>
    </div>

    <!-- Pumps -->
    <h6 ref="sectionPump" class="catalog-section-title fw-semibold mt-3">
      Pumps
    </h6>
    <div
      v-for="pump in catalog.pumps"
      :key="pump.id"
      class="catalog-item d-flex align-items-center mb-2 p-2 border rounded"
      :class="{
        'catalog-item--compatible': activeSlotAccepts === 'pump',
        'catalog-item--incompatible': activeSlot && activeSlotAccepts !== 'pump'
      }"
      role="button"
      tabindex="0"
      @click="onSelect({ ...pump, category: 'pump' })"
      @keydown.enter="onSelect({ ...pump, category: 'pump' })"
      @keydown.space.prevent="onSelect({ ...pump, category: 'pump' })"
      :aria-label="pump.name + ' — ' + pump.hp + ' HP — $' + pump.price"
    >
      <div class="catalog-icon me-2">
        <CatalogIcon category="pump" :icon="pump.icon" />
      </div>
      <div class="catalog-info">
        <div class="fw-semibold small">{{ pump.name }}</div>
        <div class="text-muted catalog-price">
          {{ pump.hp }} HP &ndash; ${{ pump.price }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { isMaterialCompatible, getFluid } from "@/data/fluids.js";
import { M_TO_FT } from "@/utils/units.js";
import CatalogIcon from "@/components/activity/CatalogIcon.vue";

const DEFAULT_PIPE_LENGTH_FT = 10;
const MIN_PIPE_LENGTH_FT = 1;
const MAX_PIPE_LENGTH_FT = 500;
const BADGE_PULSE_MS = 420;
const FT_TO_M = 1 / M_TO_FT;

const PIPE_COLORS = {
  PVC: { fill: "#86efac", stroke: "#16a34a" },
  Brass: { fill: "#fde68a", stroke: "#b45309" },
  Copper: { fill: "#fed7aa", stroke: "#c2410c" },
  Steel: { fill: "#94a3b8", stroke: "#334155" },
  "Stainless Steel": { fill: "#e2e8f0", stroke: "#64748b" }
};

const SLOT_META = {
  "suction-pipe": { label: "Suction Pipe", accepts: "pipe" },
  pump: { label: "Pump", accepts: "pump" },
  "pipe-section-1": { label: "Pipe Sec. 1", accepts: "pipe" },
  valve: { label: "Valve", accepts: "valve" },
  "pipe-section-2": { label: "Pipe Sec. 2", accepts: "pipe" },
  "elbow-1": { label: "Elbow 1", accepts: "fitting" },
  "riser-pipe": { label: "Riser", accepts: "pipe" },
  "elbow-2": { label: "Elbow 2", accepts: "fitting" },
  "delivery-pipe": { label: "Delivery Pipe", accepts: "pipe" }
};

export default {
  name: "ComponentCatalog",
  components: {
    CatalogIcon
  },
  props: {
    catalog: {
      type: Object,
      required: true
    },
    activeSlot: {
      type: String,
      default: null
    },
    // Dynamic slot metadata from the parent (preferred over SLOT_META when provided)
    activeSlotInfo: {
      type: Object,
      default: null
    },
    fluidKey: {
      type: String,
      default: null
    }
  },
  emits: ["assign-component"],
  data() {
    return {
      pendingPipe: null,
      selectedSizeIndex: 0,
      pipeLength: DEFAULT_PIPE_LENGTH_FT,
      pendingFitting: null,
      selectedFittingMaterial: null,
      badgeAnimating: false,
      badgePulseTimeout: null,
      MIN_PIPE_LENGTH_FT,
      MAX_PIPE_LENGTH_FT
    };
  },
  computed: {
    activeSlotLabel() {
      return (
        this.activeSlotInfo?.label ||
        SLOT_META[this.activeSlot]?.label ||
        this.activeSlot
      );
    },
    activeSlotAccepts() {
      return (
        this.activeSlotInfo?.accepts ||
        SLOT_META[this.activeSlot]?.accepts ||
        null
      );
    },
    fluidName() {
      const f = this.fluidKey ? getFluid(this.fluidKey) : null;
      return f ? f.name : "this fluid";
    },
    selectedSize() {
      if (!this.pendingPipe) return null;
      return this.pendingPipe.sizes[this.selectedSizeIndex];
    },
    pipeCost() {
      if (!this.selectedSize) return 0;
      return this.selectedSize.pricePerFoot * this.pipeLength;
    }
  },
  watch: {
    activeSlot(newSlot) {
      // Reset config panels when slot changes
      this.pendingPipe = null;
      this.pendingFitting = null;
      if (!newSlot) return;
      // Pulse the badge — clear any in-flight timer first to avoid stale flip
      clearTimeout(this.badgePulseTimeout);
      this.badgeAnimating = false;
      this.$nextTick(() => {
        this.badgeAnimating = true;
        this.badgePulseTimeout = setTimeout(() => {
          this.badgeAnimating = false;
        }, BADGE_PULSE_MS);
        // Scroll the compatible section into view
        const accepts = this.activeSlotAccepts;
        const refMap = {
          pipe: this.$refs.sectionPipe,
          fitting: this.$refs.sectionFitting,
          valve: this.$refs.sectionValve,
          pump: this.$refs.sectionPump
        };
        const el = refMap[accepts];
        const prefersReduced =
          typeof window !== "undefined" &&
          typeof window.matchMedia === "function" &&
          window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (el)
          el.scrollIntoView({
            behavior: prefersReduced ? "auto" : "smooth",
            block: "nearest"
          });
      });
    }
  },
  beforeUnmount() {
    clearTimeout(this.badgePulseTimeout);
  },
  methods: {
    pipeColor(material) {
      return PIPE_COLORS[material] || PIPE_COLORS["Steel"];
    },
    isMaterialOk(material) {
      if (!this.fluidKey) return true;
      return isMaterialCompatible(this.fluidKey, material);
    },
    fittingMaterialOk(fitting) {
      if (!this.fluidKey || !fitting.materials) return true;
      return fitting.materials.some((m) =>
        isMaterialCompatible(this.fluidKey, m)
      );
    },
    onPipeClick(pipe) {
      // Block only if a slot is active that doesn't accept pipes
      if (this.activeSlot && this.activeSlotAccepts !== "pipe") return;
      this.pendingPipe = pipe;
      this.pendingFitting = null;
      this.selectedSizeIndex = 0;
      this.pipeLength = DEFAULT_PIPE_LENGTH_FT;
      this.$nextTick(() => {
        if (this.$refs.pipeConfig) {
          const prefersReduced =
            typeof window !== "undefined" &&
            typeof window.matchMedia === "function" &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches;
          this.$refs.pipeConfig.scrollIntoView({
            behavior: prefersReduced ? "auto" : "smooth",
            block: "nearest"
          });
        }
      });
    },
    cancelPipe() {
      this.pendingPipe = null;
    },
    confirmPipe() {
      if (
        !this.pendingPipe ||
        !this.selectedSize ||
        this.pipeLength < MIN_PIPE_LENGTH_FT
      )
        return;
      const component = {
        ...this.pendingPipe,
        category: "pipe",
        selectedSize: this.selectedSize,
        length_ft: this.pipeLength,
        length_m: this.pipeLength * FT_TO_M,
        price: this.pipeCost
      };
      this.$emit("assign-component", {
        slotId: this.activeSlot, // null = auto-find first empty pipe slot
        component
      });
      this.pendingPipe = null;
    },
    onFittingClick(fitting) {
      // Block only if a slot is active that doesn't accept fittings
      if (this.activeSlot && this.activeSlotAccepts !== "fitting") return;
      this.pendingFitting = fitting;
      this.pendingPipe = null;
      // Default to the first compatible material, or the first material
      const compatible = fitting.materials.find((m) => this.isMaterialOk(m));
      this.selectedFittingMaterial = compatible || fitting.materials[0];
      this.$nextTick(() => {
        if (this.$refs.fittingConfig) {
          const prefersReduced =
            typeof window !== "undefined" &&
            typeof window.matchMedia === "function" &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches;
          this.$refs.fittingConfig.scrollIntoView({
            behavior: prefersReduced ? "auto" : "smooth",
            block: "nearest"
          });
        }
      });
    },
    cancelFitting() {
      this.pendingFitting = null;
    },
    confirmFitting() {
      if (!this.pendingFitting || !this.selectedFittingMaterial) return;
      const component = {
        ...this.pendingFitting,
        category: "fitting",
        material: this.selectedFittingMaterial
      };
      this.$emit("assign-component", {
        slotId: this.activeSlot,
        component
      });
      this.pendingFitting = null;
    },
    onSelect(component) {
      // Block only if a slot is active that doesn't accept this type
      if (this.activeSlot && component.category !== this.activeSlotAccepts)
        return;
      this.$emit("assign-component", {
        slotId: this.activeSlot, // null = auto-find first empty matching slot
        component
      });
    }
  }
};
</script>
